<template>
    <div data-component="OrderDetailsGroup"
         class="order-details-group">
        <!-- Group header - name/session details plus the group's redeem button. -->
        <div v-if="showHeader"
             class="order-details-group__header"
             :class="{ clickable: isCollapsible }"
             data-testid="groupHeader"
             @click="toggleGroup">
            <div v-if="group.session">
                <div v-if="group.bookingGroup"
                     class="order-details-group__badges">
                    <BookingGroupNameBadge v-if="group.bookingGroup"
                                           data-testid="groupBookingGroupName"
                                           :name="group.bookingGroup.name" />
                </div>
                <div data-testid="groupSessionName"
                     class="order-details-group__session-name">
                    {{ group.session.name }}
                </div>
                <div data-testid="groupSessionTime"
                     class="order-details-group__session-time">
                    {{ timeFilter(group.session.startTime, true) }}
                    <!-- If this session is in a different timezone display a warning indicator. -->
                    <i v-if="group.isDifferentTimezone"
                       data-testid="groupDifferentTimezoneIcon"
                       class="fa-solid fa-circle-exclamation"></i>
                    <TodayBadge data-testid="groupTodayBadge"
                                class="ml-05"
                                :date="group.session.startTime" />
                </div>
            </div>
            <div v-else
                 data-testid="groupName"
                 class="order-details-group__session-name">
                <span>
                    {{ l(group.getNameLangKey()) }}
                </span>
                <Badge v-if="group.isValidOpenTicket">
                    {{ l('VALID_ANY_DAY') }}
                </Badge>
            </div>
            <RedeemGroupButton data-testid="groupRedeemButtonWrapper"
                               :group="group"
                               :is-live-order="currentOrder.isLive()" />
        </div>

        <!-- Different timezone alert -->
        <div v-if="group.session && group.isDifferentTimezone"
             data-testid="groupDifferentTimezoneAlert"
             class="alert alert-warning alert-warning--adaptive order-details-group__warning"
             :class="{ clickable: isCollapsible }"
             @click="toggleGroup">
            <p>
                <i class="fa-solid fa-triangle-exclamation"></i>
                {{ l('ORDER_DETAILS.SESSION_DIFFERENT_TIMEZONE') }}
            </p>
            <!-- Start time at remote branch -->
            <p>
                {{ l('ORDER_DETAILS.SESSION_BRANCH_START_TIME') }}
                {{ group.items[0]?.branch?.name }}:
                <strong>{{ timeFilter(group.session.startTime, true) }}</strong>
            </p>
            <!-- Start time at local branch -->
            <p>
                {{ l('ORDER_DETAILS.SESSION_BRANCH_START_TIME') }}
                {{ currentBranchName }}:
                <strong>{{ timeFilter(group.session.startsAt, true) }}</strong>
            </p>
        </div>
        <!-- Different day alert -->
        <div v-else-if="group.session && isForSessionToday === false"
             data-testid="groupSessionNotTodayAlert"
             class="alert alert-warning alert-warning--adaptive order-details-group__warning"
             :class="{ clickable: isCollapsible }"
             @click="toggleGroup">
            <i class="fa-solid fa-triangle-exclamation"></i>
            {{ l('ORDER_DETAILS.SESSION_NOT_TODAY') }}
        </div>

        <div v-if="isCollapsible"
             class="order-details-group__count-line pa-1"
             :class="isExpanded ? 'order-details-group__count-line--expanded' : ''"
             data-testid="groupCountLine"
             @click="toggleGroup">
            <div data-testid="groupItemQty">
                <i class="fa-solid mr-025"
                   :class="itemTypeIcon"></i>
                {{ l(itemTypeCountLangKey, { count: itemQty }) }}
                <span v-if="peopleQty !== null"
                      class="ml-1">
                    <i class="fa-solid mr-025"
                       :class="[peopleQty === 1 ? 'fa-user' : 'fa-users']" />
                    {{ l(peopleCountLangKey, { count: peopleQty }) }}
                </span>
            </div>

            <ShowHideLink :show="isExpanded" />
        </div>

        <CollapseTransition :show="isContentVisible"
                            :duration-ms="openDurationMs"
                            @opened="$emit('expanded', group)">
            <div data-testid="groupTable"
                 class="order-details-group__table"
                 :class="{ 'order-details-group__table--standalone': !showHeader && !isCollapsible }">
                <OrderItemsTable :current-group="group"
                                 :current-order="currentOrder" />
            </div>
        </CollapseTransition>
    </div>
</template>

<script>
import Badge from '@/vue/components/Badges/Badge';
import BookingGroupNameBadge from '@/epos/vue/pages/order-details/components/BookingGroupNameBadge';
import CollapseTransition from '@/vue/components/Transitions/CollapseTransition';
import ItemType from '@/lib/Items/ItemType';
import OrderItemsTable from '@/epos/vue/pages/order-details/components/OrderItemsTable';
import RedeemGroupButton from '@/epos/vue/pages/order-details/components/RedeemGroupButton';
import ShowHideLink from '@/vue/components/Generic/ShowHideLink';
import TodayBadge from '@/epos/vue/components/Badges/TodayBadge';
import { getItemTypeCountLangKey, getItemTypeTabIcon } from '@/lib/Items/itemTypeTabs';
import {
    isCurrentGroupForSessionTodayCheck,
} from '@/epos/vue/pages/order-details/functions/redemptionFunctions';

// Item types where the number of people the items are for is worth showing alongside the item qty.
const ITEM_TYPES_WITH_PEOPLE = [
    ItemType.TICKET,
    ItemType.MEMBERSHIP_PLAN,
];

const OPEN_DURATION_MS = 200;
const QUICK_OPEN_DURATION_MS = 80;

export default {
    name: 'OrderDetailsGroup',

    components: {
        Badge,
        BookingGroupNameBadge,
        CollapseTransition,
        ShowHideLink,
        OrderItemsTable,
        RedeemGroupButton,
        TodayBadge,
    },

    props: {
        group: {
            type: Object,
            required: true,
        },
        currentOrder: {
            type: Object,
            required: true,
        },
        currentBranchName: {
            type: String,
            default: null,
        },
        isExpanded: {
            type: Boolean,
            default: false,
        },
        showHeader: {
            type: Boolean,
            default: true,
        },
        isCollapsible: {
            type: Boolean,
            default: true,
        },
        // Set while something is waiting for this group to open so it can scroll to it.
        // Used when jumping to a group from the summary card.
        openQuickly: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['toggle-group', 'expanded'],

    computed: {
        openDurationMs() {
            return this.openQuickly ? QUICK_OPEN_DURATION_MS : OPEN_DURATION_MS;
        },

        isContentVisible() {
            return !this.isCollapsible || this.isExpanded;
        },

        itemQty() {
            return this.countedItems.reduce((sum, item) => sum + item.qty, 0);
        },

        /**
         * @return {number|null}
         */
        peopleQty() {
            if (!ITEM_TYPES_WITH_PEOPLE.includes(this.group.itemType)) {
                return null;
            }

            return this.countedItems.reduce((sum, item) => sum + item.getPeople(), 0);
        },

        /**
         * @return {string}
         */
        peopleCountLangKey() {
            return this.group.itemType === ItemType.MEMBERSHIP_PLAN ? 'MEMBER_COUNT' : 'PEOPLE_COUNT';
        },

        /**
         * The lines that count towards the group's totals.
         *
         * Item options are excluded because they are just children of another line, and cloned
         * bundle lines because they are just display lines.
         *
         * @return {OrderLine[]}
         */
        countedItems() {
            return this.group.items.filter(
                (item) => item.itemType !== ItemType.ITEM_OPTION && !item.isClone
            );
        },

        itemTypeIcon() {
            return getItemTypeTabIcon(this.group.itemType);
        },

        itemTypeCountLangKey() {
            return getItemTypeCountLangKey(this.group.itemType);
        },

        isForSessionToday() {
            return isCurrentGroupForSessionTodayCheck(this.group);
        },
    },

    methods: {
        toggleGroup(event) {
            if (!this.isCollapsible || event.target.closest('button, a, .btn')) {
                return;
            }

            this.$emit('toggle-group', this.group);
        },
    },
};
</script>

<style lang="less">
@import "../../../../../less/variables";

.order-details-group {
    scroll-margin-top: 45px;
    transition: box-shadow 1s ease-out; // Fades out the .scroll-highlight flash.

    &:not(:first-child) {
        margin-top: @gutter-width;
    }
}

.order-details-group__header {
    padding: @gutter-width;
    font-size: 17px;
    font-weight: bold;
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);
    display: flex;
    gap: 6px;
    justify-content: space-between;
    background: @dt-dark-blue;
    color: @white;
    border-radius: @border-radius-base @border-radius-base 0 0;

    > div {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
}

.order-details-group__warning {
    padding: @gutter-width @gutter-width;
    margin-bottom: 0;
    border-width: 0 0 1px;
    border-radius: 0;
    text-align: left;
    font-size: 15px;
    font-weight: bold;

    i {
        margin-right: @quarter-gutter-width;
    }
}

.order-details-group__count-line {
    display: flex;
    justify-content: space-between;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 0 0 @border-radius-base @border-radius-base;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.22);
    }

    &--expanded {
        border-radius: 0;
    }

    @media (max-width: @mobile-max) {
        font-size: 14px;
    }
}

.order-details-group__table {
    .dt-table__cell-subtitle {
        color: @dt-grey-neutral-200;
    }
}

.order-details-group__badges {
    display: flex;
    flex-direction: row;
    gap: @half-gutter-width;
    &:not(:empty) {
        margin-bottom: 4px;
    }
}

.order-details-group__session-name {
    font-size: 20px;
    font-weight: bold;
    margin-top: -4px; // Negate some whitespace at the top of the element caused by line-height.
}

.order-details-group__session-time {
    font-size: 15px;
    // Tall enough for the badge, so a wrapped line doesn't get pushed apart by it.
    line-height: 22px;

    .label {
        vertical-align: middle;
    }
}

.order-details-group__table {
    border-top: 1px solid rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.2);
    border-radius: 0 0 @border-radius-base @border-radius-base;

    // With no header or show/hide row above it the table is the whole group.
    &--standalone {
        border-top: none;
        border-radius: @border-radius-base;
    }
}

@media (max-width: @mobile-max) {
    .order-details-group {
        background: @dt-grey-neutral-200;
        // The group is only a little lighter than the page, so a border is what actually makes it
        // read as a box.
        border: 1px solid @dt-grey-neutral-300;
        border-radius: @border-radius-base;
        overflow: hidden; // Keep the header's dark corners inside the rounded border.
    }

    .order-details-group__session-name {
        font-size: 18px;
    }

    .order-details-group__count-line:hover {
        background: rgba(0, 0, 0, 0.05);
    }

    .order-details-group__warning {
        padding: @gutter-width-mobile;
    }

    .order-details-group__table {
        padding: @gutter-width-mobile;

        .dt-table__cell-subtitle {
            color: @dt-grey-neutral-700;
        }
    }
}
</style>
